import { useMemo, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { LogOut } from 'lucide-react';

import { BrandHeader } from '@/components/layout/BrandHeader';
import { useCompanies } from '@/hooks/useConfig';
import { useCurrentPlayer, useStore, useUser } from '@/app/store';
import { pushNew } from '@/lib/firebaseClient';
import { useGoogleAuth } from '@/features/auth/useGoogleAuth';
import {
  completeProfileSchema,
  type CompleteProfileFields,
  type Player,
  type Position,
} from '@/lib/schemas';

import '@/styles/page-shell.css';
import '@/styles/register.css';

const POS_OPTIONS: { value: Position; label: string; icon: string }[] = [
  { value: 'C-Level / Director', label: 'C-Level / Director', icon: '🎖️' },
  { value: 'Manager', label: 'Manager', icon: '🧑‍💼' },
  { value: 'Tech', label: 'Tech', icon: '💻' },
  { value: 'Other', label: 'Other', icon: '👤' },
];

export function RegisterPage() {
  const navigate = useNavigate();
  const user = useUser();
  const currentPlayer = useCurrentPlayer();
  const { list: companies } = useCompanies();
  const setCurrentPlayer = useStore((s) => s.setCurrentPlayer);
  const showToast = useStore((s) => s.showToast);
  const { signOutNow } = useGoogleAuth();

  if (!user) return <Navigate to="/login" replace />;
  if (currentPlayer) return <Navigate to="/menu" replace />;

  if (!user.displayName || !user.email) {
    return (
      <div className="page-shell">
        <div className="bg-grid" />
        <div className="bg-glow glow-top" />
        <div className="wrap">
          <div className="reg-wrap">
            <div className="panel" style={{ textAlign: 'center' }}>
              <p className="panel-pre">PERFIL INCOMPLETO</p>
              <h1 className="panel-title">FALTA NOMBRE O EMAIL</h1>
              <p className="panel-sub" style={{ marginTop: 18 }}>
                Tu cuenta de Google no expone nombre o email. Cierra sesión y
                vuelve a intentarlo dando permisos completos.
              </p>
              <button
                type="button"
                className="btn btn-ghost"
                style={{ marginTop: 24 }}
                onClick={() => signOutNow()}
              >
                <LogOut className="w-[16px] h-[16px]" /> Cerrar sesión
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <CompleteProfileForm
      googleName={user.displayName}
      googleEmail={user.email}
      googleUid={user.uid}
      photoURL={user.photoURL}
      companies={companies}
      onSignOut={signOutNow}
      onSuccess={(id, player) => {
        setCurrentPlayer(id, player);
        showToast(`¡Listo, ${player.name}!`, 'success');
        navigate('/menu');
      }}
      onError={() => showToast('No se pudo registrar. Inténtalo de nuevo.', 'error')}
    />
  );
}

/* -------------------------------------------------------------------- */

interface FormProps {
  googleName: string;
  googleEmail: string;
  googleUid: string;
  photoURL: string | null;
  companies: string[];
  onSuccess: (id: string, player: Player) => void;
  onError: () => void;
  onSignOut: () => void;
}

function CompleteProfileForm({
  googleName, googleEmail, googleUid, photoURL, companies, onSuccess, onError, onSignOut,
}: FormProps) {
  const [sameAsLogin, setSameAsLogin] = useState(true);

  const {
    register,
    handleSubmit,
    control,
    setValue,
    formState: { errors, isValid, isSubmitting },
  } = useForm<CompleteProfileFields>({
    resolver: zodResolver(completeProfileSchema),
    mode: 'onChange',
    defaultValues: {
      company: '',
      phone: '',
      position: undefined,
      companyEmail: googleEmail,
    },
  });

  // Cuando el toggle "mismo del login" cambia, sincroniza companyEmail
  const handleToggleSameEmail = (checked: boolean) => {
    setSameAsLogin(checked);
    if (checked) {
      setValue('companyEmail', googleEmail, { shouldValidate: true });
    } else {
      setValue('companyEmail', '', { shouldValidate: true });
    }
  };

  const onSubmit = async (data: CompleteProfileFields) => {
    const player: Player = {
      name: googleName,
      email: googleEmail,
      companyEmail: data.companyEmail,
      company: data.company,
      phone: data.phone,
      position: data.position,
      scores: { g1: 0, g2: 0, g3: 0, g4: 0, total: 0 },
      gPlayed: { g1: false, g2: false, g3: false, g4: false },
      ts: Date.now(),
      ownerUid: googleUid,
    };
    try {
      const id = await pushNew('/players', player);
      onSuccess(id, player);
    } catch (err) {
      console.error(err);
      onError();
    }
  };

  return (
    <div className="page-shell">
      <div className="bg-grid" />
      <div className="bg-glow glow-top" />
      <div className="bg-glow glow-left" />

      <div className="wrap">
        <div className="topbar">
          <BrandHeader />
          <button type="button" className="btn btn-ghost" onClick={onSignOut} title="Cerrar sesión">
            <LogOut className="w-[16px] h-[16px]" /> CERRAR SESIÓN
          </button>
        </div>

        <div className="reg-wrap">
          <div className="panel">
            <p className="panel-pre">PASO 1 DE 1</p>
            <h1 className="panel-title">COMPLETA TU PERFIL</h1>
            <p className="panel-sub">
              Tu nombre y email vienen de Google. Sólo falta empresa, teléfono y nivel.
            </p>

            <div className="reg-google-card">
              {photoURL ? (
                <img src={photoURL} alt="" className="reg-google-avatar" />
              ) : (
                <div className="reg-google-avatar-fallback">
                  {googleName.slice(0, 1).toUpperCase()}
                </div>
              )}
              <div className="reg-google-info">
                <p className="reg-google-name">{googleName}</p>
                <p className="reg-google-email">{googleEmail}</p>
              </div>
              <span className="reg-google-tag">GOOGLE</span>
            </div>

            <form className="reg-form" onSubmit={handleSubmit(onSubmit)}>
              <Controller
                name="company"
                control={control}
                render={({ field }) => (
                  <CompanyAutoField
                    value={field.value}
                    onChange={field.onChange}
                    options={companies}
                    error={errors.company?.message}
                  />
                )}
              />

              <Controller
                name="phone"
                control={control}
                render={({ field }) => (
                  <div className="reg-field">
                    <label className="reg-label" htmlFor="phone">Teléfono</label>
                    <input
                      id="phone"
                      type="tel"
                      inputMode="numeric"
                      autoComplete="tel"
                      maxLength={12}
                      placeholder="5512345678"
                      className={`reg-input ${errors.phone ? 'err' : ''}`}
                      value={field.value ?? ''}
                      onBlur={field.onBlur}
                      onChange={(e) => {
                        const digits = e.target.value.replace(/\D/g, '').slice(0, 12);
                        field.onChange(digits);
                      }}
                    />
                    {errors.phone ? (
                      <p className="reg-err">{errors.phone.message}</p>
                    ) : (
                      <p className="reg-hint">
                        Lo usaremos para contactarte en caso de ser ganador del concurso.
                      </p>
                    )}
                  </div>
                )}
              />

              {/* Correo empresarial — opcional sincronizar con el de Google */}
              <div className="reg-field">
                <label className="reg-label" htmlFor="companyEmail">Correo empresarial</label>
                <input
                  id="companyEmail"
                  type="email"
                  autoComplete="email"
                  placeholder="tu@empresa.com"
                  readOnly={sameAsLogin}
                  className={`reg-input ${errors.companyEmail ? 'err' : ''} ${sameAsLogin ? 'locked' : ''}`}
                  {...register('companyEmail')}
                />
                <label className="reg-checkbox">
                  <input
                    type="checkbox"
                    checked={sameAsLogin}
                    onChange={(e) => handleToggleSameEmail(e.target.checked)}
                  />
                  <span>Es el mismo que mi cuenta de Google</span>
                </label>
                {errors.companyEmail && <p className="reg-err">{errors.companyEmail.message}</p>}
              </div>

              <Controller
                name="position"
                control={control}
                render={({ field }) => (
                  <div className="reg-field">
                    <span className="reg-label">Nivel / Posición</span>
                    <div className="reg-pos">
                      {POS_OPTIONS.map((opt) => (
                        <button
                          key={opt.value}
                          type="button"
                          className={`reg-pos-btn ${field.value === opt.value ? 'active' : ''}`}
                          onClick={() => field.onChange(opt.value)}
                        >
                          <span className="reg-pos-emoji">{opt.icon}</span>
                          <span>{opt.label}</span>
                        </button>
                      ))}
                    </div>
                    {errors.position && <p className="reg-err">{errors.position.message}</p>}
                  </div>
                )}
              />

              <div className="reg-actions">
                <Link to="/" className="btn btn-ghost">← Cancelar</Link>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={!isValid || isSubmitting}
                >
                  {isSubmitting ? 'Guardando...' : 'EMPEZAR →'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------- */

interface CompanyAutoProps {
  value: string;
  onChange: (v: string) => void;
  options: string[];
  error?: string;
}

function CompanyAutoField({ value, onChange, options, error }: CompanyAutoProps) {
  const [open, setOpen] = useState(false);

  const filtered = useMemo(() => {
    const q = value.trim().toLowerCase();
    if (!q) return options.slice(0, 12);
    return options.filter((c) => c.toLowerCase().includes(q)).slice(0, 12);
  }, [value, options]);

  return (
    <div className="reg-field reg-auto">
      <label className="reg-label" htmlFor="company">Empresa</label>
      <input
        id="company"
        autoComplete="off"
        placeholder="Empieza a escribir tu empresa..."
        className={`reg-input ${error ? 'err' : ''}`}
        value={value}
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
      />
      {open && filtered.length > 0 && (
        <ul className="reg-auto-list" role="listbox">
          {filtered.map((name) => (
            <li
              key={name}
              role="option"
              aria-selected={value === name}
              className="reg-auto-item"
              onMouseDown={(e) => { e.preventDefault(); onChange(name); setOpen(false); }}
            >
              {name}
            </li>
          ))}
        </ul>
      )}
      {error && <p className="reg-err">{error}</p>}
    </div>
  );
}
